/* ==========================================================================
   CART DRAWER + CART PAGE  —  Silverback Moto Co.
   Self-contained, runs without theme.js cart handlers. Owns:
     • Drawer open / close
     • Add to Cart  (intercepts any form posting to /cart/add)
     • Quantity +/- buttons + input change (debounced)  — drawer AND page
     • Remove item                                       — drawer AND page
     • Promo code apply / remove                         — drawer AND page
     • Auto-refresh via Shopify Section Rendering API
     • Header cart count bubble updates
   --------------------------------------------------------------------------
   Both the drawer and the cart page use the SAME data-* hooks
   ([data-quantity-minus], [data-promo-apply], etc.), so this single
   JS file owns interactions for both.
   ========================================================================== */

(function () {
  'use strict';

  const DRAWER_SECTION = 'cart-drawer';
  const CART_PAGE_SECTION = 'main-cart';

  const SEL = {
    drawer:          '[data-cart-drawer]',
    overlay:         '[data-cart-overlay]',
    open:            '[data-cart-open]',
    close:           '[data-cart-close]',
    cartPage:        '[data-cart-page]',
    addToCartForm:   'form[action$="/cart/add"], form[action*="/cart/add"]',
    quantityMinus:   '[data-quantity-minus]',
    quantityPlus:    '[data-quantity-plus]',
    quantityInput:   '[data-quantity-input]',
    removeItem:      '[data-remove-item]',
    promoInput:      '[data-promo-input]',
    promoApply:      '[data-promo-apply]',
    promoRemove:     '[data-promo-remove]',
    promoMessage:    '[data-promo-message]',
    cartCount:       '[data-cart-count]',
    headerBubble:    '.header__cart-count, [data-cart-bubble], [data-cart-count-bubble]',
  };

  const qtyDebounce = {};

  /* ---------- helpers ---------- */

  const $  = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  // Are we currently on the /cart page?
  function isOnCartPage() {
    return !!$(SEL.cartPage);
  }

  /* ---------- drawer open / close ---------- */

  function openDrawer() {
    const drawer  = $(SEL.drawer);
    const overlay = $(SEL.overlay);
    if (!drawer) return;
    drawer.classList.add('cart-drawer--open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('cart-drawer__overlay--open');
    document.body.setAttribute('data-cart-locked', '');
    document.dispatchEvent(new CustomEvent('cart:opened'));
  }

  function closeDrawer() {
    const drawer  = $(SEL.drawer);
    const overlay = $(SEL.overlay);
    if (!drawer) return;
    drawer.classList.remove('cart-drawer--open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.classList.remove('cart-drawer__overlay--open');
    document.body.removeAttribute('data-cart-locked');
    document.dispatchEvent(new CustomEvent('cart:closed'));
  }

  function setLoading(on) {
    const drawer = $(SEL.drawer);
    const cartPage = $(SEL.cartPage);
    if (drawer) {
      if (on) drawer.setAttribute('data-loading', '');
      else    drawer.removeAttribute('data-loading');
    }
    if (cartPage) {
      if (on) cartPage.setAttribute('data-loading', '');
      else    cartPage.removeAttribute('data-loading');
    }
  }

  /* ---------- header cart count bubble ---------- */

  function updateCartBubble(count) {
    $$(SEL.headerBubble).forEach(el => {
      el.textContent = String(count);
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  /* ---------- section refresh ---------- */
  // Refreshes whichever sections are currently mounted: drawer, cart page, or both.
  // Uses Shopify's multi-section query so it's a single round-trip.

  async function refreshCartUI() {
    setLoading(true);

    // Build the list of sections to fetch based on what's actually on the page.
    const sectionsToFetch = [];
    if ($(SEL.drawer))   sectionsToFetch.push(DRAWER_SECTION);
    if ($(SEL.cartPage)) sectionsToFetch.push(CART_PAGE_SECTION);

    if (sectionsToFetch.length === 0) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/?sections=' + sectionsToFetch.join(','), {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('section fetch failed');
      const data = await res.json();

      let newItemCount = 0;

      // ---- Drawer ----
      if (data[DRAWER_SECTION]) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data[DRAWER_SECTION], 'text/html');

        const newDrawer  = doc.querySelector(SEL.drawer);
        const newOverlay = doc.querySelector(SEL.overlay);
        const oldDrawer  = $(SEL.drawer);
        const oldOverlay = $(SEL.overlay);

        if (newDrawer && oldDrawer) {
          const wasOpen = oldDrawer.classList.contains('cart-drawer--open');
          oldDrawer.replaceWith(newDrawer);
          if (wasOpen) {
            newDrawer.classList.add('cart-drawer--open');
            newDrawer.setAttribute('aria-hidden', 'false');
          }
          newItemCount = parseInt(newDrawer.dataset.itemCount, 10) || 0;
        }
        if (newOverlay && oldOverlay) {
          const wasOpen = oldOverlay.classList.contains('cart-drawer__overlay--open');
          oldOverlay.replaceWith(newOverlay);
          if (wasOpen) newOverlay.classList.add('cart-drawer__overlay--open');
        }
      }

      // ---- Cart page ----
      if (data[CART_PAGE_SECTION]) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data[CART_PAGE_SECTION], 'text/html');
        const newPage = doc.querySelector(SEL.cartPage);
        const oldPage = $(SEL.cartPage);

        if (newPage && oldPage) {
          oldPage.replaceWith(newPage);
        }
      }

      // If we don't have a drawer on the page, derive count from cart.js for the bubble.
      if (!data[DRAWER_SECTION]) {
        try {
          const cartRes = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
          const cart = await cartRes.json();
          newItemCount = cart.item_count || 0;
        } catch (_) { /* best-effort */ }
      }

      updateCartBubble(newItemCount);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: newItemCount } }));
    } catch (err) {
      console.error('[cart] refreshCartUI failed:', err);
    } finally {
      setLoading(false);
    }
  }

  /* ---------- cart change (qty / remove) ---------- */

  async function changeLine(itemKey, quantity) {
    setLoading(true);
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: itemKey, quantity }),
      });
      if (!res.ok) throw new Error('cart/change.js failed: ' + res.status);
      await refreshCartUI();
    } catch (err) {
      console.error('[cart] changeLine failed:', err);
      setLoading(false);
    }
  }

  /* ---------- add to cart ---------- */

  async function handleAddToCart(form) {
    const submitBtn      = form.querySelector('[type="submit"], [data-add-to-cart]');
    const submitWrapper  = submitBtn && (submitBtn.closest('.product-form__submit') || submitBtn);

    setLoading(true);
    if (submitWrapper) submitWrapper.classList.add('is-loading');
    if (submitBtn)     submitBtn.disabled = true;

    openDrawer(); // open optimistically

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'add to cart failed');
      }
      await refreshCartUI();
    } catch (err) {
      console.error('[cart] add to cart failed:', err);
      const body = $('[data-cart-content]');
      if (body) {
        const note = document.createElement('p');
        note.style.cssText = 'padding:1rem;color:#FF3B30;font-size:0.875rem;text-align:center;';
        note.textContent = 'Could not add this item. Please try again.';
        body.prepend(note);
        setTimeout(() => note.remove(), 4000);
      }
      setLoading(false);
    } finally {
      if (submitWrapper) submitWrapper.classList.remove('is-loading');
      if (submitBtn)     submitBtn.disabled = false;
    }
  }

  /* ---------- promo: apply / remove ---------- */

  function setPromoMessage(text, kind) {
    // There may be two promo-message slots on screen (drawer open over cart page).
    // Update all of them.
    $$(SEL.promoMessage).forEach(el => {
      el.textContent = text || '';
      el.classList.remove('cart-drawer__promo-message--error', 'cart-drawer__promo-message--success',
                          'cart-page__promo-message--error', 'cart-page__promo-message--success');
      if (kind === 'error') {
        el.classList.add('cart-drawer__promo-message--error', 'cart-page__promo-message--error');
      }
      if (kind === 'success') {
        el.classList.add('cart-drawer__promo-message--success', 'cart-page__promo-message--success');
      }
    });
  }

  async function applyPromo(rawCode) {
    const code = (rawCode || '').trim().toUpperCase();
    if (!code) return;

    setPromoMessage('Applying…', null);
    $$(SEL.promoApply).forEach(b => b.disabled = true);

    try {
      await fetch('/discount/' + encodeURIComponent(code), {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'follow',
      });

      const cartRes = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      const cart = await cartRes.json();

      const applied = getAppliedDiscountCode(cart);
      if (applied && applied.toUpperCase() === code) {
        await refreshCartUI();
        setPromoMessage('Discount applied to subtotal.', 'success');
      } else {
        setPromoMessage('Code not valid for this cart.', 'error');
        $$(SEL.promoApply).forEach(b => b.disabled = false);
      }
    } catch (err) {
      console.error('[cart] applyPromo failed:', err);
      setPromoMessage('Could not apply code. Try again.', 'error');
      $$(SEL.promoApply).forEach(b => b.disabled = false);
    }
  }

  async function removePromo() {
    setPromoMessage('Removing…', null);
    try {
      await fetch('/discount/?redirect=' + encodeURIComponent('/cart'), {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'follow',
      });
      await refreshCartUI();
    } catch (err) {
      console.error('[cart] removePromo failed:', err);
      setPromoMessage('Could not remove. Refresh and try again.', 'error');
    }
  }

  function getAppliedDiscountCode(cart) {
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length) {
      return cart.cart_level_discount_applications[0].title;
    }
    if (cart.discount_applications && cart.discount_applications.length) {
      const dc = cart.discount_applications.find(d => d.type === 'discount_code');
      if (dc) return dc.title;
    }
    return '';
  }

  /* ---------- event delegation ---------- */

  function bindDelegated() {

    document.addEventListener('click', (e) => {
      // Open drawer  —  but ONLY if we're NOT on the /cart page (let the link navigate).
      // On the /cart page, the cart icon should still navigate to /cart (page reload).
      const opener = e.target.closest(SEL.open);
      if (opener && !e.target.closest('[data-no-drawer]')) {
        if (isOnCartPage()) {
          // On cart page, an "open cart" click is meaningless — close any open drawer.
          if ($(SEL.drawer) && $(SEL.drawer).classList.contains('cart-drawer--open')) {
            e.preventDefault();
            closeDrawer();
          }
          // Otherwise let default link behavior happen (or do nothing for buttons).
          return;
        }
        e.preventDefault();
        openDrawer();
        return;
      }

      // Close drawer
      if (e.target.closest(SEL.close) || e.target.closest(SEL.overlay)) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      // Qty −
      const minus = e.target.closest(SEL.quantityMinus);
      if (minus) {
        e.preventDefault();
        const key = minus.dataset.itemKey;
        const input = nearestQtyInput(minus, key);
        if (!input) return;
        const next = Math.max(0, (parseInt(input.value, 10) || 1) - 1);
        input.value = next;
        changeLine(key, next);
        return;
      }

      // Qty +
      const plus = e.target.closest(SEL.quantityPlus);
      if (plus) {
        e.preventDefault();
        const key = plus.dataset.itemKey;
        const input = nearestQtyInput(plus, key);
        if (!input) return;
        const next = (parseInt(input.value, 10) || 0) + 1;
        input.value = next;
        changeLine(key, next);
        return;
      }

      // Remove
      const remove = e.target.closest(SEL.removeItem);
      if (remove) {
        e.preventDefault();
        const key = remove.dataset.itemKey;
        const li = remove.closest('[data-cart-item]');
        if (li) li.setAttribute('data-removing', '');
        changeLine(key, 0);
        return;
      }

      // Promo apply
      if (e.target.closest(SEL.promoApply)) {
        e.preventDefault();
        // If both drawer + page have a promo input, the visible/last-edited one wins.
        const inputs = $$(SEL.promoInput);
        const input  = inputs.find(i => i.value.trim().length > 0) || inputs[0];
        if (input) applyPromo(input.value);
        return;
      }

      // Promo remove
      if (e.target.closest(SEL.promoRemove)) {
        e.preventDefault();
        removePromo();
        return;
      }
    });

    // INPUT  —  qty input typed change (debounced) + promo apply enable
    document.addEventListener('input', (e) => {
      const qty = e.target.closest(SEL.quantityInput);
      if (qty) {
        const key = qty.dataset.itemKey;
        clearTimeout(qtyDebounce[key]);
        qtyDebounce[key] = setTimeout(() => {
          const next = Math.max(0, parseInt(qty.value, 10) || 0);
          changeLine(key, next);
        }, 600);
        return;
      }

      const promo = e.target.closest(SEL.promoInput);
      if (promo) {
        const hasText = promo.value.trim().length > 0;
        $$(SEL.promoApply).forEach(b => b.disabled = !hasText);
        return;
      }
    });

    // KEYDOWN  —  Enter on promo input applies; Esc closes drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const drawer = $(SEL.drawer);
        if (drawer && drawer.classList.contains('cart-drawer--open')) {
          closeDrawer();
        }
      }
      if (e.key === 'Enter' && e.target.closest(SEL.promoInput)) {
        e.preventDefault();
        applyPromo(e.target.value);
      }
    });

    // SUBMIT  —  intercept any add-to-cart form on the page
    document.addEventListener('submit', (e) => {
      const form = e.target.closest(SEL.addToCartForm);
      if (!form) return;
      e.preventDefault();
      handleAddToCart(form);
    });
  }

  // Find the qty input that pairs with a +/- button — could be in drawer or page.
  // Scoped to the same item row to avoid matching across cart-drawer ↔ cart-page.
  function nearestQtyInput(button, key) {
    const row = button.closest('[data-cart-item]');
    if (row) {
      const input = row.querySelector(`[data-quantity-input][data-item-key="${key}"]`);
      if (input) return input;
    }
    // Fallback to first global match if scoping didn't find one.
    return $(`[data-quantity-input][data-item-key="${key}"]`);
  }

  /* ---------- public API ---------- */

  window.SilverbackCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshCartUI,
    add: handleAddToCart,
  };

  /* ---------- init ---------- */

  function init() {
    bindDelegated();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();